import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { getFirebaseAuth } from '../lib/firebase';
import { createUser, getUser } from '../lib/firebaseService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  const loadUserProfile = useCallback(async (uid) => {
    try {
      const userData = await getUser(uid);
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Profil yüklənə bilmədi:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const auth = getFirebaseAuth();
    
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        setFirebaseUser(fbUser);
        await loadUserProfile(fbUser.uid);
      } else {
        setFirebaseUser(null);
        setUser(null);
      }
      setLoading(false);
      setAuthReady(true);
    });

    return () => unsubscribe();
  }, [loadUserProfile]);

  const login = async (email, password) => {
    const auth = getFirebaseAuth();
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    // Profili yüklə və user state-i yenilə
    const userData = await loadUserProfile(userCredential.user.uid);
    setFirebaseUser(userCredential.user);
    return { firebaseUser: userCredential.user, userData };
  };

  const register = async (email, password, full_name, phone, store_type = 'retail', store_name = '') => {
    const auth = getFirebaseAuth();
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    await createUser(userCredential.user.uid, {
      uid: userCredential.user.uid,
      email,
      full_name,
      phone: phone || '',
      store_type: store_type,
      store_name: store_name || '',
      is_admin: false
    });
    
    const userData = await loadUserProfile(userCredential.user.uid);
    setFirebaseUser(userCredential.user);
    return { firebaseUser: userCredential.user, userData };
  };

  const logout = async () => {
    const auth = getFirebaseAuth();
    await signOut(auth);
    setUser(null);
    setFirebaseUser(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      firebaseUser,
      loading, 
      authReady,
      login, 
      register,
      logout,
      reloadUser: () => firebaseUser ? loadUserProfile(firebaseUser.uid) : null
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth AuthProvider içində istifadə edilməlidir');
  }
  return context;
};
